import { toolProgressLabel } from "./toolProgressLabels.ts";

export type AssistantSegment =
  | { type: "reasoning"; text: string; done: boolean; id?: string }
  | {
      type: "tool";
      tool: string;
      label: string;
      state: "running" | "done" | "error";
      summary?: string;
      id?: string;
    }
  | { type: "text"; text: string };

export function textFromAssistantSegments(segments: AssistantSegment[]): string {
  return segments
    .filter((segment): segment is Extract<AssistantSegment, { type: "text" }> => segment.type === "text")
    .map((segment) => segment.text)
    .join("");
}

type ToolSegment = Extract<AssistantSegment, { type: "tool" }>;

/**
 * One tool call = one timeline row. Match on a stable id (toolCallId).
 * Running creates the row; later done/error with the same id update it in place.
 * Events without a tool name must not create a generic "Arbeite…" row or clobber
 * a specific label (AI SDK `tool-output-available` has no `toolName`).
 */
export function upsertToolSegment(segments: AssistantSegment[], next: ToolSegment): AssistantSegment[] {
  const matchId = typeof next.id === "string" && next.id.trim() ? next.id.trim() : "";
  if (!matchId) {
    if (!next.tool) return segments;
    return [...segments, next];
  }

  const idx = segments.findIndex((segment) => segment.type === "tool" && segment.id === matchId);
  if (idx < 0) {
    if (!next.tool) return segments;
    return [...segments, next];
  }

  const prev = segments[idx];
  if (!prev || prev.type !== "tool") return segments;
  const copy = segments.slice();
  copy[idx] = {
    ...prev,
    ...next,
    id: matchId,
    tool: next.tool || prev.tool,
    label: next.tool ? next.label || prev.label : prev.label || next.label,
    state: next.state,
    summary: next.summary ?? prev.summary,
  };
  return copy;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function partType(part: unknown): string {
  return isRecord(part) ? String(part.type ?? "") : "";
}

function toolNameFromPart(part: Record<string, unknown>): string {
  if (typeof part.toolName === "string" && part.toolName.trim()) return part.toolName.trim();
  const type = String(part.type ?? "");
  if (type.startsWith("tool-")) return type.slice("tool-".length);
  return "";
}

function toolStateFromPart(part: Record<string, unknown>): "running" | "done" | "error" {
  const state = String(part.state ?? "");
  if (state.includes("error") || typeof part.errorText === "string") return "error";
  return "done";
}

function dataProgressFromPart(part: Record<string, unknown>): ToolSegment | null {
  if (partType(part) !== "data-tool-progress") return null;
  const data = isRecord(part.data) ? part.data : part;
  const tool = typeof data.tool === "string" ? data.tool.trim() : "";
  if (!tool) return null;
  const stateRaw = String(data.state ?? "done");
  const state: "running" | "done" | "error" =
    stateRaw === "running" || stateRaw === "error" ? stateRaw : "done";
  const summary = typeof data.summary === "string" ? data.summary : undefined;
  const id =
    (typeof part.id === "string" && part.id.trim()) ||
    (typeof data.toolCallId === "string" && data.toolCallId.trim()) ||
    "";
  return {
    type: "tool",
    tool,
    label: toolProgressLabel(tool, state),
    state,
    ...(summary ? { summary } : {}),
    ...(id ? { id } : {}),
  };
}

/**
 * Rebuilds the live timeline from persisted UI parts (reasoning, tools, text)
 * in stored order. Legacy rows without parts become a single text segment.
 */
export function assistantSegmentsFromParts(
  parts: unknown[] | null | undefined,
  fallbackText = "",
): AssistantSegment[] {
  if (!Array.isArray(parts) || parts.length === 0) {
    const text = fallbackText.trim();
    return text ? [{ type: "text", text: fallbackText }] : [];
  }

  const hasProgress = parts.some((part) => partType(part) === "data-tool-progress");
  let segments: AssistantSegment[] = [];

  for (const part of parts) {
    if (!isRecord(part)) continue;
    const type = partType(part);
    if (type === "reasoning") {
      const text = typeof part.text === "string" ? part.text : "";
      segments.push({ type: "reasoning", text, done: true });
      continue;
    }
    if (type === "text") {
      const text = typeof part.text === "string" ? part.text : "";
      if (text) segments.push({ type: "text", text });
      continue;
    }
    const fromProgress = dataProgressFromPart(part);
    if (fromProgress) {
      segments = upsertToolSegment(segments, fromProgress);
      continue;
    }
    if (hasProgress) continue;
    if (type.startsWith("tool-") || type === "dynamic-tool") {
      const tool = toolNameFromPart(part);
      if (!tool) continue;
      segments = upsertToolSegment(segments, {
        type: "tool",
        tool,
        label: toolProgressLabel(tool, toolStateFromPart(part)),
        state: toolStateFromPart(part),
        id: typeof part.toolCallId === "string" ? part.toolCallId : undefined,
      });
    }
  }

  if (segments.length === 0) {
    const text = fallbackText.trim();
    return text ? [{ type: "text", text: fallbackText }] : [];
  }
  return segments;
}
